import {
  LIVE_AGENT_CONNECTING_MESSAGE,
  LIVE_AGENT_ERROR_MESSAGE,
  LIVE_AGENT_SEED_MESSAGE_LIMIT,
} from '../../shared/constants';
import { MessageType, createMessage } from '../../shared/message';

const CONVERSATION_TYPES = [MessageType.USER, MessageType.BOT, MessageType.AGENT];

/**
 * Handles a settled Planner-bot turn: saves the bot or error message, fires image-upload
 * analytics, and starts the live-agent connect sequence if the bot escalates.
 *
 * @param {object} ctx
 * @param {object} ctx.botResponse   raw planner-assistant data (or undefined)
 * @param {any}    ctx.botError      planner-assistant error (or undefined)
 * @param {object[]} ctx.messages    the conversation so far, seeded into the agent chat
 * @param {object} ctx.lastUserMessage
 * @param {{current: any}} ctx.sessionIDRef
 * @param {{current: any}} ctx.actionIDRef
 * @param {(msg: object) => Promise<any>} ctx.saveMessage
 * @param {(v: boolean) => void} ctx.setIsSubmitting
 * @param {() => void} ctx.broadCastMessageUpdate
 * @param {(args: object, seedMessages: object[]) => Promise<any>} ctx.initiateAgentChat
 * @param {() => Promise<any>} ctx.updateConnectingToConnected
 * @param {(sessionID: any, actionID: any, outcome: string, question: any) => void} ctx.sendImageUploadAnalytics
 * @param {() => void} ctx.clearActionId  resets the provider's actionID ref
 */
export const handleBotResponse = ({
  botResponse,
  botError,
  messages,
  lastUserMessage,
  sessionIDRef,
  actionIDRef,
  saveMessage,
  setIsSubmitting,
  broadCastMessageUpdate,
  initiateAgentChat,
  updateConnectingToConnected,
  sendImageUploadAnalytics,
  clearActionId,
}) => {
  const responseData = botResponse?.retrieveDetailsPlannerAssistant || {};

  sendImageUploadAnalytics(
    sessionIDRef.current,
    actionIDRef.current,
    botError ? 'failure' : 'success',
    lastUserMessage?.content
  );

  saveMessage(createMessage(MessageType.BOT, botError ? null : { ...responseData }, {
    userMessageOrigin: lastUserMessage?.messageOrigin || '',
    isImageAttached: lastUserMessage?.isImageAttached || false,
    imageOrigin: lastUserMessage?.imageOrigin || '',
    error: botError
  }))
    .catch(() => {})
    .finally(() => {
      setIsSubmitting(false);
      broadCastMessageUpdate();
    });

  if (!botError && responseData.escalate) {
    // The agent gets the conversation so far. System notices are UI, not conversation, and the
    // shopper's last message is left out because it already rides as `text`.
    const history = messages
      .filter((msg) => msg !== lastUserMessage && CONVERSATION_TYPES.includes(msg.type))
      .slice(-LIVE_AGENT_SEED_MESSAGE_LIMIT);
    // ⚠ Chain .then() before .catch() — .catch() resolves, so a trailing .then() also runs
    // on failure. See .claude/context/live-agent.md's "PROMOTE-ONLY" section.
    saveMessage(LIVE_AGENT_CONNECTING_MESSAGE)
      .then(() => initiateAgentChat(
        { text: lastUserMessage?.content || '', actionId: actionIDRef.current },
        history,
      ))
      .then(() => updateConnectingToConnected())
      .catch(() => saveMessage(LIVE_AGENT_ERROR_MESSAGE).catch(() => {}))
      .finally(() => {
        clearActionId();
        broadCastMessageUpdate();
        return Promise.resolve();
      });
  } else {
    clearActionId();
  }
};
